import axios from 'axios';
import { Task, TaskStatus, TaskPriority } from 'public-contracts';
import { configs } from '../configs';
import authProvider from '../authProvider';

// Helper to get auth headers with access token
const getAuthHeaders = async () => {
  const token = await authProvider.auth.getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export interface TaskFilter {
  status?: TaskStatus | TaskStatus[];
  priority?: TaskPriority | TaskPriority[];
  assigneeId?: string;
  createdBy?: string;
  tags?: string[];
  dueDateFrom?: string;
  dueDateTo?: string;
}

export interface TaskStatistics {
  byStatus: Record<TaskStatus, number>;
  byPriority: Record<TaskPriority, number>;
  total: number;
}

export const taskService = {
  // Get all tasks with optional filters
  async getTasks(filters?: TaskFilter): Promise<Task[]> {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.status) {
        const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        statuses.forEach(s => params.append('status', s));
      }
      if (filters.priority) {
        const priorities = Array.isArray(filters.priority) ? filters.priority : [filters.priority];
        priorities.forEach(p => params.append('priority', p));
      }
      if (filters.assigneeId) params.append('assigneeId', filters.assigneeId);
      if (filters.createdBy) params.append('createdBy', filters.createdBy);
      if (filters.tags) {
        filters.tags.forEach(tag => params.append('tags', tag));
      }
      if (filters.dueDateFrom) params.append('dueDateFrom', filters.dueDateFrom);
      if (filters.dueDateTo) params.append('dueDateTo', filters.dueDateTo);
    }

    const headers = await getAuthHeaders();
    const response = await axios.get(`${configs.apiRoot}/tasks?${params.toString()}`, { headers });
    return response.data;
  },

  // Get task by ID
  async getTaskById(id: string): Promise<Task> {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${configs.apiRoot}/tasks/${id}`, { headers });
    return response.data;
  },

  // Create new task
  async createTask(taskData: Partial<Task>): Promise<Task> {
    const headers = await getAuthHeaders();
    const response = await axios.post(`${configs.apiRoot}/tasks`, taskData, { headers });
    return response.data;
  },

  // Update task
  async updateTask(id: string, taskData: Partial<Task>): Promise<Task> {
    const headers = await getAuthHeaders();
    const response = await axios.put(`${configs.apiRoot}/tasks/${id}`, taskData, { headers });
    return response.data;
  },

  // Update task status
  async updateTaskStatus(id: string, status: TaskStatus): Promise<Task> {
    const headers = await getAuthHeaders();
    const response = await axios.patch(`${configs.apiRoot}/tasks/${id}/status`, { status }, { headers });
    return response.data;
  },

  // Update task position
  async updateTaskPosition(id: string, position: number): Promise<Task> {
    const headers = await getAuthHeaders();
    const response = await axios.patch(`${configs.apiRoot}/tasks/${id}/position`, { position }, { headers });
    return response.data;
  },

  // Delete task
  async deleteTask(id: string): Promise<void> {
    const headers = await getAuthHeaders();
    await axios.delete(`${configs.apiRoot}/tasks/${id}`, { headers });
  },

  // Get task statistics
  async getStatistics(): Promise<TaskStatistics> {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${configs.apiRoot}/tasks/statistics`, { headers });
    return response.data;
  },
};

export default taskService;
